/**
 * iter323 — UyarilarWidget (reusable)
 *
 * İki yerde kullanılır:
 *  - Dashboard.jsx (Admin Kontrol Paneli) sabit widget
 *  - UserControlDashboard.jsx (iter326) widget_catalog içinden "reminders" tipi
 *
 * Tek veri kaynağı: GET /api/reminders → { active, history, counts, can_manage }
 *
 * Normal kullanıcı:
 *   - görür + manuel uyarıyı "Tamamlandı" yapabilir
 *   - YENİ uyarı oluşturamaz, silemez
 * Admin / System Authority:
 *   - [+ Uyarı Ekle] + silme + yönetim
 *
 * GMP sistem uyarıları (Günlük Temizlik / Sıcaklık-Nem):
 *   - Manuel "Tamamlandı" butonu YOK (gerçek GMP kaydı girilmesi beklenir)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Modal, Input, Textarea, Select, Badge } from "@/components/UI";
import { Bell, Plus, CheckCircle2, AlertTriangle, Clock, Trash2, Repeat, History } from "lucide-react";

const REC_LABEL = {
  none: "Tek Seferlik",
  daily: "Günlük",
  weekly: "Haftalık",
  monthly: "Aylık",
};

function statusBadge(occ) {
  if (occ.status === "completed") return <Badge level="ok">Tamamlandı</Badge>;
  if (occ.status === "not_done") return <Badge level="missing">Yapılmadı</Badge>;
  if (occ.is_overdue) return <Badge level="missing">Gecikti</Badge>;
  return <Badge level="attention">Aktif</Badge>;
}

function Row({ occ, canComplete, onComplete, onOpen }) {
  const sysLock = occ.is_system;
  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 cursor-pointer"
      data-testid={`rem-row-${occ.id}`}
      onClick={() => onOpen(occ)}
    >
      <span className="text-slate-400">
        {occ.recurrence === "none" ? <Clock size={12}/> : <Repeat size={12}/>}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
          {occ.title}
          {sysLock && <span className="ml-1 text-[9px] bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1 rounded">GMP</span>}
        </div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
          <span className="font-mono">{(occ.due_date || "").slice(0, 10)}</span>
          <span>·</span>
          <span>{REC_LABEL[occ.recurrence] || occ.recurrence}</span>
        </div>
      </div>
      {statusBadge(occ)}
      {canComplete && occ.status === "active" && !sysLock && (
        <button
          onClick={(e) => { e.stopPropagation(); onComplete(occ); }}
          className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 p-1 rounded"
          title="Tamamlandı"
          data-testid={`rem-complete-${occ.id}`}
        >
          <CheckCircle2 size={13}/>
        </button>
      )}
    </div>
  );
}

export default function UyarilarWidget({ compact = false, title = "Uyarılar" }) {
  const [data, setData] = useState({ active: [], history: [], counts: {}, can_manage: false });
  const [loading, setLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reminders");
      setData(r);
    } catch (e) {
      // sessiz — rate limit vs
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000); // 1 dk periyodik yenile
    return () => clearInterval(iv);
  }, [load]);

  const complete = async (occ) => {
    try {
      await api.post(`/reminders/occurrence/${occ.id}/complete`, {});
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Tamamlanamadı");
    }
  };

  const openOcc = (occ) => setOpenDetail(occ);

  const counts = data.counts || {};
  const activeCount = counts.active || 0;
  const historyCount = (counts.completed || 0) + (counts.not_done || 0);

  return (
    <Card className={`p-3 ${compact ? "" : "mb-3"}`} data-testid="uyarilar-widget">
      <div className="flex items-center gap-2 mb-2">
        <Bell size={14} className="text-indigo-600"/>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex-1">{title}</h3>
        <span
          className="text-[10px] bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded font-mono"
          data-testid="rem-counts"
          title={`Aktif ${activeCount} · Gecikti ${counts.overdue || 0} · Geçmiş ${historyCount}`}
        >
          {activeCount}🟡 · {counts.overdue || 0}🔴
        </span>
        {data.can_manage && (
          <button
            onClick={() => setOpenAdd(true)}
            className="text-[10px] px-2 py-1 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 flex items-center gap-1"
            data-testid="rem-add-btn"
          >
            <Plus size={11}/> Uyarı Ekle
          </button>
        )}
      </div>

      {/* ~10 satırlık yükseklik — iç scroll */}
      <div className="overflow-y-auto border rounded border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40" style={{ maxHeight: "360px" }} data-testid="rem-scroll">
        {/* Active */}
        <div data-testid="rem-active-list">
          {data.active.length === 0 && (
            <div className="text-xs text-center text-emerald-700 dark:text-emerald-400 py-4" data-testid="rem-active-empty">
              ✓ Aktif uyarı yok
            </div>
          )}
          {data.active.map((o) => (
            <Row key={o.id} occ={o}
              canComplete={true}
              onComplete={complete}
              onOpen={openOcc}
            />
          ))}
        </div>

        {/* History divider + list */}
        {data.history.length > 0 && (
          <>
            <div className="sticky top-0 flex items-center gap-1 bg-slate-100 dark:bg-slate-800 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 border-t border-b border-slate-200 dark:border-slate-700" data-testid="rem-history-divider">
              <History size={10}/> Geçmiş / Tamamlanan ({data.history.length})
            </div>
            <div data-testid="rem-history-list">
              {data.history.map((o) => (
                <Row key={o.id} occ={o}
                  canComplete={false}
                  onComplete={() => {}}
                  onOpen={openOcc}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {openAdd && data.can_manage && (
        <AddReminderModal onClose={() => setOpenAdd(false)} onSaved={() => { setOpenAdd(false); load(); }}/>
      )}
      {openDetail && (
        <DetailModal occ={openDetail} canManage={data.can_manage} onClose={() => setOpenDetail(null)}
          onDeleted={() => { setOpenDetail(null); load(); }}
        />
      )}
    </Card>
  );
}

function AddReminderModal({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ title: "", description: "", recurrence: "none", reminder_date: today });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.title.trim()) { alert("Başlık zorunlu"); return; }
    if (!f.reminder_date) { alert("Tarih zorunlu"); return; }
    setBusy(true);
    try {
      await api.post("/reminders", {
        title: f.title.trim(),
        description: f.description?.trim() || null,
        recurrence: f.recurrence,
        reminder_date: f.reminder_date,
      });
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="Yeni Uyarı Ekle" size="md">
      <div className="space-y-3">
        <Input label="Başlık *" value={f.title}
          onChange={(e) => setF({ ...f, title: e.target.value })}
          placeholder="Örn: Ambalaj deposunu kontrol et"
          data-testid="rem-add-title" autoFocus/>
        <Textarea label="Açıklama" value={f.description} rows={3}
          onChange={(e) => setF({ ...f, description: e.target.value })}
          placeholder="İsteğe bağlı detay"
          data-testid="rem-add-desc"/>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Tekrar *" value={f.recurrence}
            onChange={(e) => setF({ ...f, recurrence: e.target.value })}
            data-testid="rem-add-recurrence">
            <option value="none">Tek Seferlik</option>
            <option value="daily">Günlük</option>
            <option value="weekly">Haftalık</option>
            <option value="monthly">Aylık</option>
          </Select>
          <Input label={f.recurrence === "none" ? "Tarih *" : "Başlangıç Tarihi *"} type="date"
            value={f.reminder_date}
            onChange={(e) => setF({ ...f, reminder_date: e.target.value })}
            data-testid="rem-add-date"/>
        </div>
        <div className="text-[11px] bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-500/30 rounded p-2 text-indigo-800 dark:text-indigo-300">
          <AlertTriangle size={11} className="inline mr-1"/>
          Tekrarlı uyarılarda yeni periyot başlayınca önceki tamamlanmamış kayıt otomatik "Yapılmadı" olarak kapanır.
          Tek seferlik uyarılar tarih geçse de kullanıcı tamamlayana kadar aktif kalır (Gecikti).
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
        <Btn onClick={save} disabled={busy} data-testid="rem-add-save">
          <Plus size={12}/> {busy ? "Kaydediliyor..." : "Kaydet"}
        </Btn>
      </div>
    </Modal>
  );
}

function DetailModal({ occ, canManage, onClose, onDeleted }) {
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/reminders/occurrence/${occ.id}`);
        setTpl(r);
      } catch { /* silent */ }
    })();
  }, [occ.id]);

  const deleteTemplate = async () => {
    const rid = tpl?.template?.id;
    if (!rid) return;
    if (!window.confirm("Bu uyarı şablonu silinsin mi? Geçmiş kayıtlar korunacak; yeni periyot oluşmayacak.")) return;
    setBusy(true);
    try {
      await api.del(`/reminders/${rid}`);
      onDeleted();
    } catch (e) {
      alert(e.response?.data?.detail || "Silinemedi");
    } finally { setBusy(false); }
  };

  const KV = ({ label, children }) => (
    <div>
      <div className="text-[10px] uppercase text-slate-500 tracking-wider">{label}</div>
      <div className="text-sm text-slate-800 dark:text-slate-100">{children || "—"}</div>
    </div>
  );

  return (
    <Modal open={true} onClose={onClose} title={`Uyarı Detayı — ${occ.title}`} size="md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <KV label="Başlık">{occ.title}</KV>
          <KV label="Durum">{statusBadge(occ)}</KV>
          <KV label="Tekrar">{REC_LABEL[occ.recurrence] || occ.recurrence}</KV>
          <KV label="Tarih">{(occ.due_date || "").slice(0, 10)}</KV>
          <KV label="Kaynak">{occ.is_system ? "Sistem (GMP)" : "Manuel"}</KV>
          <KV label="Periyot">{occ.period_key}</KV>
        </div>
        {occ.description && (
          <div>
            <div className="text-[10px] uppercase text-slate-500 tracking-wider mb-1">Açıklama</div>
            <div className="text-sm text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded p-2 whitespace-pre-wrap">
              {occ.description}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
          {tpl?.template && (
            <>
              <KV label="Oluşturan">{tpl.template.created_by_name}</KV>
              <KV label="Oluşturulma">{(tpl.template.created_at || "").slice(0, 16).replace("T", " ")}</KV>
            </>
          )}
          {occ.completed_by_name && (
            <>
              <KV label="Tamamlayan">{occ.completed_by_name}</KV>
              <KV label="Tamamlanma">{(occ.completed_at || "").slice(0, 16).replace("T", " ")}</KV>
            </>
          )}
          {occ.closed_at && occ.status === "not_done" && (
            <KV label="Kapanma">{(occ.closed_at || "").slice(0, 16).replace("T", " ")}</KV>
          )}
        </div>
        {occ.is_system && (
          <div className="text-[11px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded p-2 text-amber-800 dark:text-amber-300">
            Bu bir sistem uyarısıdır. Manuel "Tamamlandı" seçeneği yoktur — gerçek GMP kaydı girildiğinde otomatik tamamlanır.
          </div>
        )}
      </div>
      <div className="flex justify-between gap-2 mt-4">
        <div>
          {canManage && tpl?.template && occ.source === "manual" && (
            <Btn variant="secondary" onClick={deleteTemplate} disabled={busy} data-testid="rem-detail-delete">
              <Trash2 size={12}/> Şablonu Sil
            </Btn>
          )}
        </div>
        <Btn variant="secondary" onClick={onClose}>Kapat</Btn>
      </div>
    </Modal>
  );
}
